import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import {
  INotificationComplete,
  INotificationContent,
  INotificationsArchiveResponse,
  INotificationsResponse,
} from 'services/notifications/types/types';
import { IInfinitePageParams } from 'services/types';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';

import { useInfiniteQuery } from '@tanstack/react-query';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';

export const useGetUnreadNotification = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<number, DefaultError>({
    queryKey: ['get-unread-notification '],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Notification/UserNotifications/UnreadCount`,
      });

      return responseHandler(response, {
        groupName: `useGetUnreadNotification`,
      });
    },
    enabled: enabled,
    retry: false,
    staleTime: 60 * 60 * 1000,
  });
};

export const useCompleteNotification = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useCompleteNotification'
  );
  return useMutation<boolean, DefaultError, INotificationComplete>({
    mutationFn: async (body: INotificationComplete) => {
      loading(i18next.t('service-message.complete.loading'));

      const response = await apiClient({
        method: 'POST',
        url: 'Notification/Complete',
        body,
      });

      return responseHandler(response, {
        groupName: 'useCompleteNotification',
      });
    },
    onSuccess: data => {
      success(i18next.t('service-message.complete.success'));
    },
    onError: dismiss,
  });
};

export const useVisitNotification = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useVisitNotification');
  return useMutation<boolean, DefaultError, INotificationComplete>({
    mutationFn: async (body: INotificationComplete) => {
      loading(i18next.t('service-message.visit.loading'));

      const response = await apiClient({
        method: 'POST',
        url: 'Notification/UserNotifications/Visit',
        body,
      });

      return responseHandler(response, {
        groupName: 'useVisitNotification',
      });
    },
    onSuccess: data => {
      success(i18next.t('service-message.visit.success'));
    },
    onError: dismiss,
  });
};

export const useGetNotificationListInfinite = (params: IInfinitePageParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<INotificationContent[], DefaultError>({
    queryKey: ['notifications', params],

    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Notification/UserNotifications',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<INotificationsResponse>(response, {
        groupName: `useGetNotificationListInfinite - Page: ${pageParam}`,
      });

      return infiniteHandler<INotificationContent>(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetNotificationArchiveListInfinite = (
  params: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<INotificationContent[], DefaultError>({
    queryKey: ['notifications-archive', params],

    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Notification/UserNotifications/Archived',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<INotificationsArchiveResponse>(
        response,
        {
          groupName: `useGetNotificationArchiveListInfinite - Page: ${pageParam}`,
        }
      );

      return infiniteHandler(value);
    },

    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetNotificationArchiveList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<INotificationContent[], DefaultError>({
    queryKey: ['get-archived-notification '],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Notification/UserNotifications/Archived`,
      });

      return responseHandler(response, {
        groupName: `useGetNotificationArchiveList`,
      });
    },
    enabled: enabled,
  });
};

export const usePutNotificationsReadAll = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutNotificationsReadAll'
  );
  return useMutation<undefined, DefaultError>({
    mutationFn: async body => {
      loading(i18next.t('service-message.visit.loading'));
      const response = await apiClient({
        method: 'PUT',
        url: `Notification/ReadAll`,
      });

      return responseHandler(response, {
        groupName: 'usePutNotificationsReadAll',
        notFoundPath: '/notifications',
      });
    },
    onSuccess: data => {
      success(i18next.t('service-message.visit.success'));
    },
    onError: dismiss,
  });
};

export const usePutNotificationsArchiveAll = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutNotificationsArchiveAll'
  );
  return useMutation<undefined, DefaultError>({
    mutationFn: async body => {
      loading(i18next.t('service-message.complete.loading'));
      const response = await apiClient({
        method: 'PUT',
        url: `Notification/ArchiveAll`,
      });

      return responseHandler(response, {
        groupName: 'usePutNotificationsArchiveAll',
        notFoundPath: '/notifications',
      });
    },
    onSuccess: data => {
      success(i18next.t('service-message.complete.success'));
    },
    onError: dismiss,
  });
};
